<template>
  <div v-if="hasRound">
    <div>
      <div v-if="voteSent" class="p--lg container--border">
        <h1 class="pb--md">Bijna klaar...</h1>
        <p class="pb--lg">
          <strong class="text--accent">Check je mailbox</strong> voor een bevestigingsmail en klik daarin op
          de link om jouw stem definitief te maken. Doe je dat niet, dan kunnen we jouw stem helaas niet
          meetellen.
        </p>
        <p class="pb--lg">
          <strong>Geen mail ontvangen? Kijk dan even in je spam folder.</strong>
        </p>
      </div>

      <template v-else>

        <div class="breadcrumb ml--lg" style="margin-top: 48px;">
          <router-link :to="{ name: 'home' }">Categorie</router-link>
          <span>Inzenden</span>
        </div>

        <div class="p--md container--border">
          <h1 class="p--md title" >Inzenden</h1>
          <div class="row">

            <template v-for="choice in selection">
              <div class="col col--1-3" :key="choice.category.name">
                <h3 class="badge badge--gold">
                  {{ choice.category.name }}
                </h3>
                <router-link
                  v-if="choice.nominee"
                  class="item item--nominee"
                  :to="urlForCategory(choice.category)"
                >
                  <div class="item__body item__body--loaded" :style="stylesForNominee(choice.nominee)"></div>
                  <h3>{{ choice.nominee.name }}</h3>
                </router-link>
                <router-link
                  v-else-if="choice.suggestion"
                  class="item item--nominee"
                  :to="urlForCategory(choice.category)"
                >
                  <div class="item__body item__body--loaded item__body--no-image"></div>
                  <h3>{{ choice.suggestion.suggestionName }}</h3>
                </router-link>
                <router-link v-else class="item item--empty" :to="urlForCategory(choice.category)">
                  <span>Kies</span>
                </router-link>
              </div>
            </template>
          </div>

          <form class="form p--md" style="margin-top: 2rem" @submit.prevent="submit" v-if="anySelected">
            <div class="form__field">
              <div class="checkbox--container">
                <checkbox v-model="agreed" :disabled="sending"/>
                <label class="form__label" for="form__field_conditions">
                  Ik ga akkoord met de
                  <a
                    target="_blank"
                    href="https://www.avrotros.nl/article/reglementen-radio-ring-stemsite-2025~1357/"
                  >
                    voorwaarden
                  </a>
                  *
                </label>
              </div>
              <p v-if="didNotAgree" class="text--error">
                U moet nog akkoord gaan met voorwaarden
              </p>
            </div>
            <div class="form__field">
              <div class="checkbox--container">
                <checkbox v-model="keepUpToDate" :disabled="sending"/>
                <label class="form__label" for="form__field_update">
                  Ja, ik wil graag op de hoogte worden gehouden van de RadioRing en andere AVROTROS-programma's
                </label>
              </div>
            </div>
            <div class="form__field">
              <input
                type="email"
                required
                class="form__input mt--md mail__input"
                ref="emailInput"
                placeholder="E-mailadres *"
                v-model="email"
                :disabled="sending"
                id="form__field_email"
              />
              <p v-if="hasInvalidEmail" class="text--error">
                Vul een geldig e-mailadres in
              </p>
            </div>
            <p style="font-size: x-small;">
              <i>
                Velden met een * zijn verplicht. Het is mogelijk om vaker te stemmen per e-mailadres, maar de
                reeds uitgebrachte stem wordt dan automatisch overschreven door de nieuwe stem.
              </i>
            </p>

            <div class="form__field" style="display: grid; justify-content: end; margin-top: 1rem;">
              <invisible-recaptcha
                :sitekey="captchaSiteKey"
                :validate="validate"
                :callback="submit"
                class="button"
                type="submit"
                :disabled="sending || isIncomplete()"
              >
                Inzenden
              </invisible-recaptcha>
            </div>

          </form>
          <p class="p--md" v-else>
            <router-link :to="{ name: 'home' }">Selecteer</router-link>
            eerst een of meer genomineerden.
          </p>
        </div>
      </template>
    </div>
  </div>
  <p v-else class="p--md" v-html="offlineDescription" />
</template>

<script>
import { post } from '@/util/request'
import InvisibleRecaptcha from 'vue-invisible-recaptcha'
import Checkbox from './Checkbox.vue'

const captchaSiteKey = process.env.CAPTCHA_KEY

export default {
  name: 'Submit',

  components: {
    'invisible-recaptcha': InvisibleRecaptcha,
    'checkbox': Checkbox,
  },

  data() {
    return {
      email: '',
      agreed: false,
      keepUpToDate: false,
      sending: false,
      voteSent: false,
      hasVotingError: false,
      hasInvalidEmail: false,
      didNotAgree: false,
      captchaSiteKey
    }
  },

  computed: {
    round() {
      return this.$store.state.rounds
    },

    offlineDescription() {
      return this.$store.state.frontend.offline_description ?
        this.$store.state.frontend.offline_description :
        'Er kan momenteel niet gestemd worden.'
    },

    hasRound() {
      return typeof this.round === 'object'
    },

    selectedNominees() {
      return this.$store.state.selected.filter(selected => {
        return typeof selected.nomineeId !== 'undefined'
      })
    },

    selectedSuggestions() {
      const selected = this.$store.state.selected.filter(selected => {
        return typeof selected.suggestionName !== 'undefined'
      })

      return selected.map(({ suggestionName, suggestionStation, categoryId,...obj }) => ({
        name: suggestionName,
        secondary_name: suggestionStation || '-',
        categoryId: categoryId
      }))
    },

    selection() {
      return this.$store.getters.selection
    },

    anySelected() {
      return this.selection.some(({ nominee, suggestion }) => nominee || suggestion)
    }
  },

  methods: {
    isValidEmail() {
      return this.$refs.emailInput.validity.valid
    },

    isIncomplete() {
      return (
        this.agreed === false ||
        this.isValidEmail() === false
      )
    },

    validate() {
      return this.isIncomplete() === false
    },

    submit(captchaHash) {
      if (this.isIncomplete()) {
        this.hasInvalidEmail = this.isValidEmail() === false
        this.didNotAgree = this.agreed === false

        return
      }

      const round = this.round

      const data = {
        captchaHash,
        roundId: round.id,
        ...(this.selectedNominees.length > 0 ? { votes: this.selectedNominees } : {}),
        ...(this.$store.state.allowSuggestions ? { suggestions: this.selectedSuggestions } : {}),
        email: this.email,
        extraFields: {
          keepUpToDate: this.keepUpToDate,
        }
      }

      this.sending = true

      post('voting/vote', {
        data: JSON.stringify(data)
      })
        .then(response => {
          if (response.ok) {
            response.json().then(({ data }) => {
              if (data.saved) {
                this.voteSent = true
                this.$store.dispatch('clearSelected')
              } else {
                this.hasVotingError = true
              }
            })
          } else {
            this.hasVotingError = true
          }

          this.sending = false
        })
        .catch(() => {
          this.hasVotingError = true
          this.sending = false
        })
    },

    stylesForNominee(nominee) {
        if (nominee.image) {
          return {
            backgroundImage: `url(${nominee.image})`
          }
        } else if(!nominee.image) {
          return {
            backgroundImage: 'url(/static/rr_fallback.png)'
          }
      }
    },

    urlForCategory(category) {
      return {
        name: 'category',
        params: {
          categoryId: category.id
        }
      }
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/Submit.vue